import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Layout, SubmitButton } from '@/shared/components';
import { useFormSubmission } from '@/shared/hooks/useFormSubmission';
import { pricesApi } from '@/shared/api';
import {
  priceCategorySchema,
  type PriceCategoryFormData
} from '@/shared/schemas';

/**
 * Страница создания категории цен
 * Форма для добавления новой категории с валидацией
 */
export const PriceCategoryCreatePage: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<PriceCategoryFormData>({
    resolver: zodResolver(priceCategorySchema),
    defaultValues: {
      name: '',
      sortOrder: 0,
      isActive: true
    }
  });

  /**
   * Хук для управления отправкой формы с уведомлениями
   */
  const { isSubmitting: isFormSubmitting, handleSubmit: handleFormSubmit } =
    useFormSubmission<PriceCategoryFormData>({
      onSubmit: async (data: PriceCategoryFormData) => {
        const response = await pricesApi.createCategory(data);

        if (response.success) {
          // Перенаправляем на страницу цен через 2 секунды
          setTimeout(() => {
            navigate('/prices');
          }, 2000);
        }
      },
      successMessage: 'Категория успешно создана',
      errorMessage: 'Ошибка при создании категории'
    });

  /**
   * Обработчик отмены
   */
  const handleCancel = (): void => {
    navigate('/prices');
  };

  return (
    <Layout title="Добавить категорию цен">
      <div className="space-y-6">
        {/* Заголовок */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-base-content">
              Добавить категорию цен
            </h2>
            <p className="text-base-content/60 mt-1">
              Создание новой категории для группировки услуг
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="btn btn-secondary"
            disabled={isSubmitting || isFormSubmitting}
          >
            <i className="fas fa-arrow-left"></i>
            Назад к списку
          </button>
        </div>

        {/* Форма */}
        <div className="card card-bordered bg-base-100 shadow-sm">
          <div className="card-body p-2">
            <form
              onSubmit={handleSubmit(handleFormSubmit)}
              className="space-y-6"
            >
              {/* Название */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Название категории <span className="text-error">*</span>
                  </span>
                </label>
                <input
                  type="text"
                  {...register('name')}
                  className={`input input-bordered w-full ${errors.name ? 'input-error' : ''}`}
                  placeholder="Например: Консультации педиатра"
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.name.message}
                    </span>
                  </label>
                )}
              </div>

              {/* Порядок сортировки */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">
                    Порядок сортировки
                  </span>
                </label>
                <input
                  type="number"
                  {...register('sortOrder', { valueAsNumber: true })}
                  className={`input input-bordered w-full ${errors.sortOrder ? 'input-error' : ''}`}
                  placeholder="0"
                  min="0"
                  disabled={isSubmitting}
                />
                {errors.sortOrder && (
                  <label className="label">
                    <span className="label-text-alt text-error">
                      {errors.sortOrder.message}
                    </span>
                  </label>
                )}
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Чем меньше число, тем выше в списке будет отображаться
                    категория
                  </span>
                </label>
              </div>

              {/* Статус активности */}
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-3">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="checkbox checkbox-primary"
                    disabled={isSubmitting}
                  />
                  <span className="label-text font-medium">
                    Активная категория
                  </span>
                </label>
                <label className="label">
                  <span className="label-text-alt text-base-content/60">
                    Неактивные категории не отображаются на сайте
                  </span>
                </label>
              </div>

              {/* Кнопки действий */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <SubmitButton
                  isLoading={isSubmitting || isFormSubmitting}
                  loadingText="Создание..."
                  icon="fas fa-save"
                  className="flex-1 sm:flex-none"
                >
                  Создать категорию
                </SubmitButton>

                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-secondary flex-1 sm:flex-none"
                  disabled={isSubmitting || isFormSubmitting}
                >
                  <i className="fas fa-times"></i>
                  Отмена
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};
